import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Table,
  Title,
  Tooltip,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useStore } from "../store";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import {
  IconDownload,
  IconFileUpload,
  IconInfoCircle,
  IconPackages,
} from "@tabler/icons-react";
import ImportStatusBadge from "./ImportStatusBadge";
import { useInterval } from "@mantine/hooks";
import FileSize from "./FileSize";
import { getImports, downloadFile } from "../api";
import { useEffect, useState } from "react";
import ImportProductsModal from "../Pim/Schemas/ImportProductsModal";

function ImportTypeBadge({ type }) {
  const { t } = useTranslation("imports");
  const names = {
    AsinFile: t("list.typeAsinFile"),
    Foreign: t("list.typeForeign"),
    Domestic: t("list.typeDomestic"),
  };
  const name = names[type] || type;
  return (
    <Badge color="gray" radius="xs">
      {name}
    </Badge>
  );
}

function ImportList() {
  const { t } = useTranslation("imports");
  const [, setLocation] = useLocation();
  const imports = useStore((state) => state.imports);
  const setImports = useStore((state) => state.setImports);
  const profile = useStore((state) => state.profile);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  // in at least one instance, this has crashed with 'some is not a function' (on a relatively modern Chrome),
  // very confusing
  const hasActiveTasks = imports?.some((imp) => imp.status === "processing");

  const interval = useInterval(
    () => {
      getImports().then((data) => {
        setImports(data);
      });
    },
    hasActiveTasks ? 5_000 : 60_000
  );

  // Initial data fetching
  useEffect(() => {
    getImports().then((data) => {
      setImports(data);
    });
  }, []);

  // Interval management
  useEffect(() => {
    if (imports) {
      // Only start interval once imports are loaded
      interval.start();
    }
    return () => interval.stop(); // Cleanup on unmount or when dependencies change
  }, [imports, hasActiveTasks, interval]);

  if (!imports) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="lg">
      <Group justify="space-between">
        <Title>{t("list.title")}</Title>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconPackages size={16} />}
            onClick={() => setBundleModalOpen(true)}
          >
            {t("list.importBundle")}
          </Button>
          <Button
            component={Link}
            to="/imports/upload"
            variant="default"
            leftSection={<IconFileUpload size={16} />}
          >
            {t("list.importFile")}
          </Button>
        </Group>
      </Group>
      <Table
        style={{ cursor: "pointer" }}
        striped
        highlightOnHover
        tabularNums
        mt="lg"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("list.colCreated")}</Table.Th>
            <Table.Th>
              {t("list.colStatus")}
              {/* &nbsp;
              <Tooltip label={<FileStatusTooltip />}>
                <IconInfoCircle size={16} stroke={1} />
              </Tooltip> */}
            </Table.Th>
            <Table.Th>{t("list.colType")}</Table.Th>
            <Table.Th>{t("list.colFileName")}</Table.Th>
            <Table.Th ta="right">{t("list.colSize")}</Table.Th>
            <Table.Th ta="right">
              {t("list.colSkus")}&nbsp;
              <Tooltip label={t("list.skusTooltip")}>
                <IconInfoCircle size={16} stroke={1} />
              </Tooltip>
            </Table.Th>
            {/* <Table.Th>Issues</Table.Th> */}
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {imports.map((importData) => (
            <Table.Tr
              key={importData.id}
              onClick={() => setLocation(`/imports/${importData.id}`)}
            >
              <Table.Td>
                <Timeago date={importData.created} />
              </Table.Td>
              <Table.Td>
                <ImportStatusBadge status={importData.status} />
              </Table.Td>
              <Table.Td>
                <ImportTypeBadge type={importData.type} />
              </Table.Td>
              <Table.Td>{importData.originalName}</Table.Td>
              <Table.Td ta="right">
                <FileSize size={importData.fileSize} />
              </Table.Td>
              <Table.Td ta="right">{importData.rowCount}</Table.Td>
              <Table.Td ta="right">
                <Tooltip label={t("list.downloadOriginal")} position="bottom">
                  <ActionIcon
                    variant="subtle"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      downloadFile(`imports/${importData.id}/file`);
                    }}
                  >
                    <IconDownload size={14} stroke={1} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <ImportProductsModal
        opened={bundleModalOpen}
        onClose={() => setBundleModalOpen(false)}
        targetTenantId={profile?.tenantId}
      />
    </Container>
  );
}

export default ImportList;
